import React, { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import { ICustomerCard, ICustomerCardType } from "../../../../models/customer-card";
import { InitContextProps, useAuthContext } from "../../../../services/context/authentication.context";
import CustomerCardService from '../../../../services/customer-card.service';
import { isNotNullOrUndefined } from "common/data-util";

interface ChangeCustomerCardInfoModalProps {
  selectedCard: ICustomerCard | null;
  handleModalHide: () => void;
  onSubmitSuccess: () => void;
}

export function ChangeCustomerCardInfoModal(props: ChangeCustomerCardInfoModalProps) {
  const { state }: InitContextProps = useAuthContext();
  const { customerId, customer } = state;

  const initialLimit = (props.selectedCard && isNotNullOrUndefined(props.selectedCard.monthlyLimit)) ?  `${props.selectedCard.monthlyLimit}` : '';

  const [saving, setSaving]: [boolean, any] = useState(false);
  const [savingError, setSavingError]: [boolean, any] = useState(false);
  const [errorMessage, setErrorMessage]: [string | null, any] = useState(null);
  const [validated, setValidated]: [boolean, any] = useState(false);
  const [limit, setLimit]: [string, Function] = useState(initialLimit);
  const [isBlocked, setIsBlocked] = useState<boolean>(props.selectedCard ? props.selectedCard.isBlocked : false)
  const [pinCode, setPinCode] = useState<string>('');

  const confirmSubmit = () => {
    setValidated(false);
    const isValidForm = validateForm();
    setValidated(true);
    if (isValidForm) {
      handleSubmit();
    } else {
      setSaving(false);
    }
  };

  const parseMonthlyLimitChange = (newValue: string) => {
    let parsedValue = '';
    if (newValue && newValue.trim().length > 0) {
      parsedValue = newValue.replace(',', '.');
    }

    setLimit(parsedValue)
  };

  const onPinChange = (newValue: string) => {
    if (newValue.length > 4 || (newValue.length && isNaN(Number(newValue)))) {
      return;
    }
    setPinCode(newValue);
  }

  const validateForm = () => {
    if (props.selectedCard && !props.selectedCard.isEidCard) {
      return validateCard()
    }

    return validateEidCard();
  };

  const isLimitInValidFormat = () => {
    if (limit.trim().length > 0 && limit.match("^[0-9]*?[.,]?[0-9]+$") === null) return false;
    return true;
  }

  const validateCard = () => {
    if(!isLimitModifiable) return true;
  
    if(isInvalidLimitFormat) return false;
    if(isBlankLimit) return false;
    if((parseFloat(limit) === props.selectedCard?.monthlyLimit && props.selectedCard.isBlocked === isBlocked)) return false;
    
    return true;
  }

  const validateEidCard = () => {
    if (pinCode.length !== 4) {
      return false;
    }
    return true;
  }

  const handleSubmit = () => {
    setSaving(true);
    setSavingError(false);

    if (!customerId || !customer || (customer && !customer.contactId) || !props.selectedCard)
      return;
    if (props.selectedCard.isEidCard) {
      CustomerCardService.changeIdCreditPin(customerId, {
          remoteCardId: props.selectedCard.remoteCardId,
          newPin: pinCode
      }).then(() => {
          setSaving(false);
          props.onSubmitSuccess();
          setTimeout(() => {
              props.handleModalHide();
          }, 300)
      }).catch((reason: any) => {
          console.error('Saving failed', reason);
          setSaving(false);
          setSavingError(true);
          if (reason.message) {
            setErrorMessage('Viga: ' + reason.message);
          }
      });  
    } else {
      CustomerCardService.postCardLimit(customerId, {
          remoteCardId: props.selectedCard.remoteCardId,
          newLimit: limit,
          isBlocked: isBlocked
      }, state.customer!.contactId).then(() => {
          setSaving(false);
          props.onSubmitSuccess();
          setTimeout(() => {
              props.handleModalHide();
          }, 300)
      }).catch((reason: any) => {
          console.error('Saving failed', reason);
          setSaving(false);
          setSavingError(true);
          if (reason.message) {
            setErrorMessage('Viga: ' + reason.message);
          }
      });      
    }

  };

  if (!props.selectedCard) return null;

  const isEidCard = props.selectedCard.isEidCard;
  const isDiscountCard = props.selectedCard.cardType === ICustomerCardType.DISCOUNT_CARD;
  const newLimitEqualsOld = !isEidCard && limit === props.selectedCard.monthlyLimit?.toString();
  const isDisabled = saving || ((newLimitEqualsOld || isDiscountCard) && isBlocked === props.selectedCard.isBlocked);
  // Discount cards carry no monthly limit, so only the blocked state can be changed for them.
  const isLimitModifiable = !isDiscountCard && !props.selectedCard.isBlocked && !isBlocked;
  const isInvalidLimitFormat = !isLimitInValidFormat();
  const isBlankLimit = !isNotNullOrUndefined(limit) || limit.length === 0;

  return (
    <>
      <Modal
        show={true}
        size={"lg"}
        onHide={props.handleModalHide}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Muuda {isEidCard ? 'kaardi PIN-i' : 'kaarti'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form 
            noValidate 
            validated={validated} 
            onSubmit={(e: any) => e.preventDefault()}
            id={"card-form"}
            className={'d-flex flex-column'}
          >
            <Form.Group 
              controlId={'form-card-number'} 
              className={'col-sm-6 p-0'}
            >
              <Form.Label>
                &nbsp;
              </Form.Label>              
              <Form.Control className={''}
                value={(props.selectedCard && (props.selectedCard.name || props.selectedCard.remoteCardId)) || ''}
                disabled={true}
                type="text" />
            </Form.Group>
            {
              isEidCard ? (
                <Form.Group 
                  controlId={'form-card-month-limit'}
                  className={'col-sm-6 p-0'}
                >
                  <Form.Label>
                      <span className="text-nowrap">Uus PIN</span>
                  </Form.Label>
                  <Form.Control
                    value={pinCode}
                    disabled={saving}
                    pattern="^[0-9]{4}"
                    onChange={(e) => onPinChange(e.target.value)}
                    type="password"
                    autoComplete="off"
                  />
                  <Form.Control.Feedback className={'text-right'} type="invalid">Vigane number</Form.Control.Feedback>
                </Form.Group>
              ) : (
                <>
                  {
                    !isDiscountCard && (
                      <Form.Group
                          controlId={'form-card-month-limit'}
                          className={'col-sm-6 p-0'}
                        >
                        <Form.Label>
                            <span className="text-nowrap">Kuu limiit</span>
                        </Form.Label>
                        <Form.Control
                          value={limit}
                          required
                          pattern="^[0-9]*?[.,]?[0-9]+$"
                          disabled={saving || !isLimitModifiable}
                          onChange={(e) => parseMonthlyLimitChange(e.target.value)}
                          type="text"
                        />
                          {
                            validated && isInvalidLimitFormat && (<Form.Control.Feedback className={'text-right'} type="invalid">Vigane number</Form.Control.Feedback>)
                          }
                          {
                            validated && isBlankLimit && (<Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>)
                          }
                      </Form.Group>
                    )
                  }
                  <Form.Group
                    controlId="form-card-blocked"  
                    className="d-flex flex-row"
                  >
                    <Form.Check
                      custom
                      type="checkbox"
                      label=""
                      checked={isBlocked}
                      required={false}
                      onChange={(e: any) => {
                        if(isBlocked){
                          setIsBlocked(false)
                        }else{
                          setIsBlocked(true);
                          setLimit(initialLimit)
                        }
                      }}
                    />
                    <Form.Label>
                      <span className="text-nowrap">Blokeeri kaart</span>
                    </Form.Label>
                  </Form.Group>
                </>
              )
            }
          </Form>
        </Modal.Body>
        <Modal.Footer className="d-flex flex-row align-items-end justify-content-around flex-wrap mb-1">
          <Button variant="outline-dark"
            onClick={() => props.handleModalHide()}
            type="button"
            disabled={saving}
            className={"d-flex align-items-center"}>
            <span className='px-2'>Loobu</span>
          </Button>
          <Button variant="primary" onClick={confirmSubmit} type="button" disabled={isDisabled}
            className="d-flex align-items-center">
            {
              !saving &&
              <span className='px-2'>Muuda</span>
            }
            {
              saving &&
              <>
                <span className='px-2'>Salvestab…</span>
                <Spinner size={"sm"} animation={"border"} />
              </>
            }
          </Button>
        </Modal.Footer>
        {
          !saving && savingError &&
          <span className="d-flex justify-content-end text-danger px-5 pb-2">
            {errorMessage || 'Viga salvestamisel, proovi mõne hetke pärast uuesti.'}
          </span>
        }
      </Modal>
    </>
  )
}